import { useEffect } from "react";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const getMeta = (attr, key) =>
  document.head.querySelector(`meta[${attr}="${key}"]`)?.getAttribute("content") ?? "";

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const getCanonical = () =>
  document.head.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? "";

/**
 * Met à jour le titre de l'onglet, l'URL canonique et les balises Open Graph /
 * Twitter de la page. Restaure les valeurs par défaut du site en quittant la page.
 * Note : les robots d'aperçu (Facebook, LinkedIn) n'exécutent pas JavaScript —
 * ils verront les balises par défaut du site définies dans index.html.
 */
export function usePageMeta(meta) {
  const title = meta?.title;
  const description = meta?.description;
  const image = meta?.image;

  useEffect(() => {
    if (!title) return undefined;

    const prev = {
      title: document.title,
      description: getMeta("name", "description"),
      ogTitle: getMeta("property", "og:title"),
      ogDescription: getMeta("property", "og:description"),
      ogImage: getMeta("property", "og:image"),
      ogUrl: getMeta("property", "og:url"),
      twitterTitle: getMeta("name", "twitter:title"),
      twitterDescription: getMeta("name", "twitter:description"),
      twitterImage: getMeta("name", "twitter:image"),
      canonical: getCanonical(),
    };

    const fullTitle = title.includes("Eugénie") ? title : `${title} · Eugénie Larrivée`;
    const url = window.location.origin + window.location.pathname;
    document.title = fullTitle;
    if (description) setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    if (description) setMeta("property", "og:description", description);
    if (image) setMeta("property", "og:image", image);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", fullTitle);
    if (description) setMeta("name", "twitter:description", description);
    if (image) setMeta("name", "twitter:image", image);
    setCanonical(url);

    return () => {
      document.title = prev.title;
      setMeta("name", "description", prev.description);
      setMeta("property", "og:title", prev.ogTitle);
      setMeta("property", "og:description", prev.ogDescription);
      setMeta("property", "og:image", prev.ogImage);
      setMeta("property", "og:url", prev.ogUrl);
      setMeta("name", "twitter:title", prev.twitterTitle);
      setMeta("name", "twitter:description", prev.twitterDescription);
      setMeta("name", "twitter:image", prev.twitterImage);
      if (prev.canonical) setCanonical(prev.canonical);
    };
  }, [title, description, image]);
}
